import dayjs from 'dayjs';

export function getEstablishedYearOptions() {
  const currentYear = dayjs().year();
  const yearSequence = [...Array(currentYear - 1989).keys()];
  const years = yearSequence.map((difference) => ({
    label: currentYear - difference,
    value: currentYear - difference,
  }));

  return years;
}

// The third badge the team awards is named for the site (owner, 6 Oct 2026):
// Seattle Food Truck, Austin Food Truck, Food Carts Portland. Keyed by the
// city config's city_name. spec/script/truck_badge_names_spec.rb fails when
// a city in config/ has no name here.
export const FAVS_BADGE_NAMES = {
  Seattle: 'SFT Favs',
  Austin: 'AFT Favs',
  Portland: 'FCP Favs',
};

// `config` is the city config (ConfigContext).
export function getFavsBadgeName(config) {
  return FAVS_BADGE_NAMES[config && config.city_name] || null;
}

// Badges for a truck card. most_liked, rising_star and sft_favs are awarded
// by the team (admin-set booleans on the truck), the ownership flags are the
// vendor's own, and most_booked is computed server-side (Truck#most_booked?).
// Every field read here has to be in the truck payload the caller renders
// from. The public truck page (TruckHighlights), the one caller, has all of
// them.
export function getTruckBadgeMap(truck, config) {
  const establishedYear = dayjs().set('year', parseInt(truck.established_year));
  const age = dayjs().diff(establishedYear, 'years');

  return [
    {
      description: 'Food truck',
      imageName: 'truck',
      key: 'truck',
      show: !truck.trailer && !truck.cart,
    }, {
      description: 'Food trailer',
      imageName: 'trailer',
      show: truck.trailer,
    }, {
      description: 'Food cart',
      imageName: 'cart',
      show: truck.cart,
    }, {
      description: 'Takes credit cards',
      imageName: 'cards',
      show: truck.accepts_credit_cards,
    }, {
      description: `Est. ${truck.established_year}`,
      imageName: 'rookie',
      show: truck.established_year && age <= 2,
    }, {
      description: `Est. ${truck.established_year}`,
      imageName: 'professional',
      show: truck.established_year && age > 2 && age <= 5,
    }, {
      description: `Est. ${truck.established_year}`,
      imageName: 'veteran',
      show: truck.established_year && age > 5,
    }, {
      description: 'Asian Owned',
      imageName: 'asian-owned',
      show: truck.asian_owned,
    }, {
      description: 'Black Owned',
      imageName: 'black-owned',
      show: truck.black_owned,
    }, {
      description: 'Latinx Owned',
      imageName: 'latinx-owned',
      show: truck.latinx_owned,
    }, {
      description: 'LGBTQ+ Owned',
      imageName: 'lgbtq-owned',
      show: truck.lgbtq_owned,
    }, {
      description: 'Veteran Owned',
      imageName: 'veteran-owned',
      show: truck.veteran_owned,
    }, {
      description: 'Women Owned',
      imageName: 'women-owned',
      show: truck.woman_owned,
    }, {
      description: 'Most Booked',
      imageName: 'most-booked',
      show: truck.most_booked,
    }, {
      description: 'Most Liked',
      imageName: 'most-liked',
      show: truck.most_liked,
    }, {
      description: 'Rising Star',
      imageName: 'rising-star',
      show: truck.rising_star,
    }, {
      // Not drawn without a name: a city missing from FAVS_BADGE_NAMES.
      description: getFavsBadgeName(config),
      imageName: 'sft-favs',
      show: truck.sft_favs && getFavsBadgeName(config),
    }, {
      // The public truck record says yes or no (health_permit_verified,
      // insurance_verified) and nothing more about a truck's documents; a
      // payload that carries the statuses themselves counts too.
      description: 'Health Permit Verified',
      imageName: 'health-permit',
      show: truck.health_permit_verified
        || truck.health_status === 'approved' || truck.health_county2_status === 'approved',
    }, {
      description: 'Fully Licensed & Insured',
      imageName: 'licensed-insured',
      show: truck.insurance_verified || truck.coi_status === 'approved',
    }, {
      description: 'Contactless Payment Accepted',
      imageName: 'contactless',
      show: truck.accepts_apple_pay || truck.accepts_google_pay,
    }
  ].filter(({ show }) => show);
}
